import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCommunityUsers } from '../hooks/useCommunityUsers';
import { User, Plus, X, Eye, EyeOff, Phone, Mail, Shield, Edit, Trash2, Search, Users } from 'lucide-react';

interface CommunityUserData {
  phone: string;
  email: string;
  password: string;
  role: 'COMMITTEE' | 'VENDOR';
}

interface CommunityUser {
  USER_ID: number;
  PHONE: string;
  EMAIL: string;
  ROLE: string;
  CREATED_AT: string;
  createdBy: number;
}

const CommunityUserManagement: React.FC = () => {
  const { user } = useAuth();
  const {
    communityUsers,
    loading: fetchLoading,
    error: fetchError,
    currentPage,
    totalPages,
    totalUsers,
    fetchCommunityUsers,
    createCommunityUser,
  } = useCommunityUsers();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<CommunityUserData>({
    phone: '',
    email: '',
    password: '',
    role: 'COMMITTEE',
  });
  const [showPassword, setShowPassword] = useState(false);

  // Check if user is a community admin
  const isCommunityAdmin = user && (user.role === 'COMMITTEE_ADMIN' || user.role === 'VENDOR_ADMIN');
  const userRole = user?.role;
  const canAddCommittee = userRole === 'COMMITTEE_ADMIN';
  const canAddVendor = userRole === 'VENDOR_ADMIN';

  // Check if user has permission
  if (!user || !isCommunityAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="font-runtime text-2xl font-bold text-black mb-2">
            Access Denied
          </h2>
          <p className="font-lucy-rose text-gray-600">
            You need community admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await createCommunityUser(formData);
      setSuccess('Committee user created successfully! OTP sent for verification.');
      setShowAddUserModal(false);
      
      // Reset form
      setFormData({
        phone: '',
        email: '',
        password: '',
        role: 'COMMITTEE',
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowAddUserModal(false);
    setFormData({
      phone: '',
      email: '',
      password: '',
      role: 'COMMITTEE',
    });
    setError(null);
    setSuccess(null);
  };

  // Filter users based on search term
  const filteredUsers = communityUsers.filter(communityUser =>
    communityUser.PHONE.toLowerCase().includes(searchTerm.toLowerCase()) ||
    communityUser.EMAIL.toLowerCase().includes(searchTerm.toLowerCase()) ||
    communityUser.ROLE.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-8">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-6">
            <div className="flex-1">
              <h1 className="font-runtime text-2xl sm:text-3xl font-bold text-black mb-2">
                Committee User Management
              </h1>
              <p className="font-lucy-rose text-gray-600 text-sm sm:text-base">
                Manage community users in the Durga Puja Network
              </p>
            </div>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="bg-dpn-pink hover:bg-dpn-raspberry text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg transition-all duration-200 font-lucy-rose font-medium flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm sm:text-base">Add Committee User</span>
            </button>
          </div>

          {/* Admin Info */}
          <div className="bg-gradient-to-r from-dpn-pink to-dpn-raspberry rounded-lg p-6 text-white">
            <div className="flex items-center space-x-3">
              <Users className="h-8 w-8" />
              <div>
                <h3 className="font-runtime text-lg font-semibold">
                  {userRole === 'COMMITTEE_ADMIN' ? 'Committee Admin Dashboard' : 'Vendor Admin Dashboard'}
                </h3>
                <p className="font-lucy-rose text-sm opacity-90">
                  Logged in as: {user.phone} ({user.role})
                </p>
                <p className="font-lucy-rose text-xs opacity-75 mt-1">
                  You can add {userRole === 'COMMITTEE_ADMIN' ? 'Committee' : 'Vendor'} users to your community
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Global Success/Error Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-600 font-lucy-rose">{success}</p>
          </div>
        )}

        {/* Combine errors from hook and local state */}
        {(error || fetchError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600 font-lucy-rose">{error || fetchError}</p>
          </div>
        )}

        {/* Search and Stats */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 gap-4">
            <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 flex-1">
              <div className="relative flex-1 sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                <input
                  type="text"
                  placeholder="Search community users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose text-sm sm:text-base"
                />
              </div>
              <div className="text-xs sm:text-sm text-gray-600 font-lucy-rose text-center sm:text-left bg-gray-50 px-3 py-2 rounded-lg sm:bg-transparent sm:px-0 sm:py-0">
                {communityUsers.length} community users
              </div>
            </div>
          </div>
        </div>

        {/* Committee Users List */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h2 className="font-runtime text-lg sm:text-xl font-bold text-black">
              Committee Users
            </h2>
          </div>

          {fetchLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink mx-auto mb-4"></div>
              <p className="font-lucy-rose text-gray-600">Loading community users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="font-lucy-rose text-gray-600">
                {searchTerm ? 'No community users found matching your search.' : 'No community users found.'}
              </p>
              <p className="font-lucy-rose text-sm text-gray-500 mt-2">
                Start by adding your first community user using the button above.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-lucy-rose">
                        User Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-lucy-rose">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-lucy-rose">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((communityUser) => (
                      <tr key={communityUser.USER_ID} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="bg-dpn-pink bg-opacity-10 p-2 rounded-lg mr-3">
                              <User className="h-5 w-5 text-dpn-pink" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 font-lucy-rose">
                                {communityUser.EMAIL}
                              </div>
                              <div className="text-sm text-gray-500 font-lucy-rose">
                                {communityUser.PHONE}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-lucy-rose ${
                            communityUser.ROLE === 'COMMITTEE' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {communityUser.ROLE}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-lucy-rose">
                          {new Date(communityUser.CREATED_AT).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden">
                <div className="divide-y divide-gray-200">
                  {filteredUsers.map((communityUser) => (
                    <div key={communityUser.USER_ID} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="bg-dpn-pink bg-opacity-10 p-2 rounded-lg mr-3 flex-shrink-0">
                            <User className="h-5 w-5 text-dpn-pink" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 font-lucy-rose truncate">
                              {communityUser.EMAIL}
                            </div>
                            <div className="text-sm text-gray-500 font-lucy-rose">
                              {communityUser.PHONE}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-lucy-rose w-fit ${
                          communityUser.ROLE === 'COMMITTEE' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {communityUser.ROLE}
                        </span>
                        <div className="text-xs text-gray-500 font-lucy-rose">
                          Created: {new Date(communityUser.CREATED_AT).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add Committee User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 lg:p-8 relative max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>

            <div className="text-center mb-6 sm:mb-8 pr-8">
              <h2 className="font-runtime text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-2">
                Add Committee User
              </h2>
              <p className="font-lucy-rose text-gray-600 text-sm sm:text-base">
                Create a new community user account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose text-sm sm:text-base"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose text-sm sm:text-base"
                    placeholder="user@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="role" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Role *
                </label>
                <select
                  id="role"
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full py-2.5 sm:py-3 px-3 sm:px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose text-sm sm:text-base"
                >
                  {canAddCommittee && (
                    <option value="COMMITTEE">Committee Member</option>
                  )}
                  {canAddVendor && (
                    <option value="VENDOR">Vendor</option>
                  )}
                </select>
              </div>

              <div>
                <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Password *
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose text-sm sm:text-base"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm font-lucy-rose">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-600 text-sm font-lucy-rose">{success}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center text-sm sm:text-base"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                ) : null}
                {loading ? 'Creating user...' : 'Create Committee User'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityUserManagement;
